/* Confirmation banner after LanguageContext auto-switches via combined
 * IP + Accept-Language detection. Copy adapts to the detection reason:
 *
 *   • reason=ip_browser_agree → silent (no banner) — high confidence switch
 *   • reason=browser_wins     → "We noticed your browser prefers {X} — kept"
 *                               with revert=ip_language (country's language)
 *   • reason=ip_only          → "Switched to {X}" with revert=previous
 *   • reason=browser_only     → "Switched to {X}" with revert=previous
 *   • reason=default          → silent (we kept en)
 *
 * Auto-hides after 10 s. One-shot per session (sessionStorage dedupe).
 */
import { useEffect, useState } from "react";
import { X, Globe } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import { DEFAULT_LANGUAGES } from "../../landing/constants";

const SESSION_FLAG = "trp_arrival_banner_dismissed";
const AUTO_HIDE_MS = 10000;

export function LanguageArrivalBanner() {
  const { setLanguage } = useLanguage();
  const [evt, setEvt] = useState(null);

  useEffect(() => {
    try { if (sessionStorage.getItem(SESSION_FLAG)) return; } catch {}
    const handler = (e) => {
      try { if (sessionStorage.getItem(SESSION_FLAG)) return; } catch {}
      // Silent for "we are 100% correct" cases — no banner.
      if (e.detail?.reason === "ip_browser_agree") return;
      if (e.detail?.reason === "default") return;
      setEvt(e.detail);
    };
    window.addEventListener("trp:lang-auto-switched", handler);
    return () => window.removeEventListener("trp:lang-auto-switched", handler);
  }, []);

  useEffect(() => {
    if (!evt) return;
    const h = setTimeout(() => {
      try { sessionStorage.setItem(SESSION_FLAG, "1"); } catch {}
      setEvt(null);
    }, AUTO_HIDE_MS);
    return () => clearTimeout(h);
  }, [evt]);

  if (!evt) return null;

  const toMeta = DEFAULT_LANGUAGES.find((l) => l.code === evt.to);
  if (!toMeta) return null;

  // Pick the "revert" target based on reason. For browser_wins we let the user
  // switch back to the IP-country's language (the country they're currently
  // physically in) — that's the more useful alternative than "English".
  const revertTo =
    evt.reason === "browser_wins"
      ? evt.ip_language
      : evt.from;
  const revertMeta = DEFAULT_LANGUAGES.find((l) => l.code === revertTo);

  // Headline copy: browser-wins gets a warmer, more informative message.
  const headline =
    evt.reason === "browser_wins" ? (
      <>Using <strong>{toMeta.name}</strong> (your browser preference)</>
    ) : (
      <>Switched to <strong>{toMeta.name}</strong></>
    );

  const revert = () => {
    if (revertTo) setLanguage(revertTo);
    try { sessionStorage.setItem(SESSION_FLAG, "1"); } catch {}
    setEvt(null);
  };

  const dismiss = () => {
    try { sessionStorage.setItem(SESSION_FLAG, "1"); } catch {}
    setEvt(null);
  };

  return (
    <div className="studio-arrival-banner" data-testid="studio-arrival-banner" data-reason={evt.reason}>
      <span className="studio-arrival-banner-flag" aria-hidden="true">
        {evt.reason === "browser_wins" ? <Globe size={16} /> : toMeta.flag}
      </span>
      <span>{headline}</span>
      {revertMeta && revertTo !== evt.to && (
        <button
          type="button"
          onClick={revert}
          className="studio-arrival-banner-apply"
          data-testid="studio-arrival-banner-revert"
        >
          <span aria-hidden="true" style={{ marginRight: 4 }}>{revertMeta.flag}</span>
          Use {revertMeta.name}
        </button>
      )}
      <button
        type="button"
        onClick={dismiss}
        className="studio-arrival-banner-dismiss"
        data-testid="studio-arrival-banner-dismiss"
        aria-label="Dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
}
